import type {
  DepartmentCompanyMappingOrigin,
  DepartmentCompanyMappingRecord,
} from './types';

export type {
  DepartmentCompanyMappingOrigin,
  DepartmentCompanyMappingStatus,
} from './types';

/**
 * The browser demo used this label as a key in `catalog.settings`. It is kept
 * only as a migration marker; the domain model never stores mappings in the
 * generic settings record.
 */
export const DEPARTMENT_COMPANY_MAPPING_SETTING_KEY = '部门-公司映射';
export const DEPARTMENT_COMPANY_MAPPING_MIGRATION_TIME =
  '2026-08-01T00:00:00.000Z';

export function disposableDepartmentId(departmentName: string): string {
  const normalized = departmentName.trim();
  if (!normalized) throw new Error('部门名称不能为空');
  return `department:${encodeURIComponent(normalized)}`;
}

/** Disposable-only company identity used by the local seed and migration. */
export function disposableCompanyId(companyName: string): string {
  const normalized = companyName.trim();
  if (!normalized) throw new Error('公司名称不能为空');
  return `company:${encodeURIComponent(normalized)}`;
}

export function departmentCompanyMappingId(departmentId: string): string {
  const normalized = departmentId.trim();
  if (!normalized) throw new Error('部门 ID 不能为空');
  return `department-company-mapping:${normalized}`;
}

export function departmentDirectoryFromNames(
  departmentNames: readonly string[],
): Map<string, string> {
  const directory = new Map<string, string>();
  for (const name of departmentNames) {
    const normalized = name.trim();
    if (!normalized) continue;
    const departmentId = disposableDepartmentId(normalized);
    const previous = directory.get(departmentId);
    if (previous && previous !== normalized) {
      throw new Error(`部门 ID ${departmentId} 绑定了多个名称`);
    }
    directory.set(departmentId, normalized);
  }
  return directory;
}

function isCanonicalIsoTimestamp(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  try {
    return new Date(value).toISOString() === value;
  } catch {
    return false;
  }
}

function isMappingOrigin(
  value: unknown,
): value is DepartmentCompanyMappingOrigin {
  return (
    value === 'fixture' ||
    value === 'legacy-settings-migration' ||
    value === 'local-save' ||
    value === 'formal-adapter'
  );
}

export function isDepartmentCompanyMappingRecord(
  value: unknown,
): value is DepartmentCompanyMappingRecord {
  if (!value || typeof value !== 'object') return false;
  const record = value as Partial<DepartmentCompanyMappingRecord>;
  return (
    typeof record.id === 'string' &&
    record.id.trim().length > 0 &&
    typeof record.departmentId === 'string' &&
    record.departmentId.trim().length > 0 &&
    typeof record.departmentName === 'string' &&
    record.departmentName.trim().length > 0 &&
    typeof record.companyId === 'string' &&
    record.companyId.trim().length > 0 &&
    typeof record.companyName === 'string' &&
    record.companyName.trim().length > 0 &&
    (record.status === 'active' || record.status === 'inactive') &&
    typeof record.resourceVersion === 'string' &&
    record.resourceVersion.trim().length > 0 &&
    isCanonicalIsoTimestamp(record.updatedAt) &&
    isMappingOrigin(record.origin)
  );
}

export function validateDepartmentCompanyMappings(
  mappings: readonly DepartmentCompanyMappingRecord[],
  departmentDirectory?: ReadonlyMap<string, string>,
): void {
  const ids = new Set<string>();
  const departmentIds = new Set<string>();
  for (const mapping of mappings) {
    if (!isDepartmentCompanyMappingRecord(mapping)) {
      throw new Error('部门-公司映射记录结构无效');
    }
    if (
      mapping.origin !== 'formal-adapter' &&
      mapping.id !== departmentCompanyMappingId(mapping.departmentId)
    ) {
      throw new Error('Disposable 部门-公司映射 ID 必须稳定绑定部门 ID');
    }
    if (departmentIds.has(mapping.departmentId)) {
      throw new Error('同一部门不能重复映射公司');
    }
    if (ids.has(mapping.id)) throw new Error('部门-公司映射 ID 重复');
    if (departmentDirectory) {
      if (!departmentDirectory.has(mapping.departmentId)) {
        throw new Error(`部门 ${mapping.departmentName} 不在当前成员目录`);
      }
      if (
        departmentDirectory.get(mapping.departmentId) !== mapping.departmentName
      ) {
        throw new Error(`部门 ${mapping.departmentId} 的名称绑定无效`);
      }
    }
    ids.add(mapping.id);
    departmentIds.add(mapping.departmentId);
  }
}

export interface LegacyDepartmentCompanySettingRow {
  key: string;
  title: string;
  defaultValue: string | number | boolean;
  enabled: boolean;
}

/**
 * Migrate only the old settings shape that can be proven from the current
 * department directory. Unknown, duplicate or empty rows are rejected rather
 * than guessed into a formal organization relationship.
 */
export function migrateLegacyDepartmentCompanyMappings(
  rows: readonly LegacyDepartmentCompanySettingRow[],
  departmentDirectory: ReadonlyMap<string, string>,
): DepartmentCompanyMappingRecord[] {
  const migrated = rows.map((row) => {
    const departmentName = row.key.trim();
    const companyName =
      typeof row.defaultValue === 'string' ? row.defaultValue.trim() : '';
    if (!departmentName || !companyName) {
      throw new Error('旧部门-公司映射包含空值，无法迁移');
    }
    const departmentId = disposableDepartmentId(departmentName);
    if (departmentDirectory.get(departmentId) !== departmentName) {
      throw new Error(`旧部门 ${departmentName} 不在当前成员目录`);
    }
    return {
      id: departmentCompanyMappingId(departmentId),
      departmentId,
      departmentName,
      companyId: disposableCompanyId(companyName),
      companyName,
      status: row.enabled ? ('active' as const) : ('inactive' as const),
      resourceVersion: 'local-1',
      updatedAt: DEPARTMENT_COMPANY_MAPPING_MIGRATION_TIME,
      origin: 'legacy-settings-migration' as const,
    };
  });
  validateDepartmentCompanyMappings(migrated, departmentDirectory);
  return migrated;
}

export function nextDepartmentCompanyMappingVersion(
  resourceVersion: string,
): string {
  const match = /^local-(\d+)$/.exec(resourceVersion);
  if (!match) {
    throw new Error('正式部门-公司映射版本必须由服务端返回，不能由 Mock 递增');
  }
  return `local-${Number(match[1]) + 1}`;
}
